import * as React from "react";
import { Loader, LoaderPropsT } from "@talentpair/quantic";
import { Error500 } from "@talentpair/sentry";
import { useFetcher, FetchT, UseFetcher } from "@talentpair/api";

interface PropsFetcherProps<D> {
  fetch: FetchT<D>;
  render: (state: UseFetcher<D>) => React.ReactElement | null;
  showError?: boolean;
  fetchKey?: string;
}

export default function PropsFetcher<D>({
  render,
  fetch,
  showError = false,
  fetchKey,
}: PropsFetcherProps<D>): React.ReactElement | null {
  const state = useFetcher<D>(fetch, fetchKey);
  if (state.err) {
    if (!showError) throw state.err;
    return <Error500 />;
  }
  return render(state);
}

interface PropsFetcherLoaderProps<D> extends Omit<PropsFetcherProps<D>, "render"> {
  render: (data: D, refetch: () => void) => React.ReactElement | null;
  LoaderProps?: LoaderPropsT;
}

export function PropsFetcherLoader<D>({
  fetch,
  render,
  showError = false,
  LoaderProps,
}: PropsFetcherLoaderProps<D>): React.ReactElement | null {
  return (
    <PropsFetcher
      fetch={fetch}
      showError={showError}
      render={({ rsp, loading, refetch }) => {
        if (loading) return <Loader {...LoaderProps} />;
        // Ternary is only for typescript. `rsp` will never actually be null here because the only time when
        // `loading` is false and `rsp` is null is when there is an error, in which case
        // <PropsFetcher> either handled it or threw an error.
        return rsp ? render(rsp, refetch) : null;
      }}
    />
  );
}
